export const FREE_BOARD_STATUS_OPTIONS = ["todo", "doing", "blocked", "done"];

export const FREE_BOARD_FILTER_FIELDS = [
    "status",
    "startAt",
    "dueDate",
    "assignee",
    "members",
    "deliverables",
    "creatorUser",
    "createdAt",
    "updatedAt",
    "doneAt",
    "task_type",
];

export const DEFAULT_FREE_BOARD_COLUMNS = [
    {id: "backlog", titleKey: "free-board.column.backlog", taskStatus: "todo"},
    {id: "in-progress", titleKey: "free-board.column.in-progress", taskStatus: "doing"},
    {id: "review", titleKey: "free-board.column.review", taskStatus: "doing"},
];

export function normalizeFreeBoardColumns(value, translate = (key) => key) {
    const configured = Array.isArray(value)
        ? value.filter((column) => column && column.id && (column.title || column.label))
        : [];
    const source = configured.length ? configured : DEFAULT_FREE_BOARD_COLUMNS;

    return source.map((column, index) => ({
        id: String(column.id),
        title: String(column.title || column.label || translate(column.titleKey) || `${index + 1}`),
        taskStatus: FREE_BOARD_STATUS_OPTIONS.includes(column.taskStatus) ? column.taskStatus : "todo",
        showSubtasks: column.showSubtasks !== false,
    }));
}

export function freeBoardColumnForTask(task, columns, columnIdField = "freeBoardColumnId") {
    const list = Array.isArray(columns) ? columns : [];
    if (!list.length) return null;
    const explicit = list.find((column) => String(column.id) === String(task?.[columnIdField] || ""));
    if (explicit) return explicit;
    return list.find((column) => column.taskStatus === task?.taskStatus) || list[0];
}

export function serializeFreeBoardColumns(columns) {
    return (Array.isArray(columns) ? columns : []).map(({id, title, taskStatus, showSubtasks}) => ({
        id: String(id),
        title: String(title || "").trim(),
        taskStatus: FREE_BOARD_STATUS_OPTIONS.includes(taskStatus) ? taskStatus : "todo",
        showSubtasks: showSubtasks !== false,
    }));
}

const entityKey = (entity) => String(
    entity?.documentId
    ?? entity?.id
    ?? entity?.member?.documentId
    ?? entity?.member?.id
    ?? entity
    ?? "",
);

function nativeDate(value) {
    if (!value) return null;
    const date = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

function dayBounds(value) {
    const start = new Date(value);
    start.setHours(0, 0, 0, 0);
    const end = new Date(value);
    end.setHours(23, 59, 59, 999);
    return {start, end};
}

export function freeBoardDateRange(preset, now = new Date(), customRange) {
    const today = dayBounds(now);
    if (preset === "today") return today;
    if (preset === "yesterday") {
        const value = new Date(today.start);
        value.setDate(value.getDate() - 1);
        return dayBounds(value);
    }
    if (preset === "this-week" || preset === "last-week") {
        const daysSinceSaturday = (today.start.getDay() + 1) % 7;
        const start = new Date(today.start);
        start.setDate(start.getDate() - daysSinceSaturday - (preset === "last-week" ? 7 : 0));
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        return {start, end};
    }
    if (preset === "last-14-days") {
        const start = new Date(today.start);
        start.setDate(start.getDate() - 13);
        return {start, end: today.end};
    }
    if (preset === "last-month") {
        const start = new Date(today.start.getFullYear(), today.start.getMonth() - 1, 1);
        const end = new Date(today.start.getFullYear(), today.start.getMonth(), 0, 23, 59, 59, 999);
        return {start, end};
    }
    if (preset === "custom") {
        const rawStart = nativeDate(customRange?.start);
        const rawEnd = nativeDate(customRange?.end);
        if (!rawStart && !rawEnd) return null;
        const first = rawStart && rawEnd && rawStart > rawEnd ? rawEnd : rawStart;
        const second = rawStart && rawEnd && rawStart > rawEnd ? rawStart : rawEnd;
        const start = first ? dayBounds(first).start : null;
        const end = second ? dayBounds(second).end : null;
        return {start, end};
    }
    return null;
}

function dateMatches(value, preset, customRange, now) {
    const date = nativeDate(value);
    if (!date) return false;
    const range = freeBoardDateRange(preset, now, customRange);
    if (!range) return true;
    return (!range.start || date >= range.start) && (!range.end || date <= range.end);
}

function relationMatches(value, selected) {
    const selectedKey = entityKey(selected);
    if (!selectedKey) return true;
    return entityKey(value) === selectedKey || entityKey(value?.member) === selectedKey;
}

export function filterFreeBoardTasks(tasks, filters = {}, now = new Date()) {
    const bucket = filters.bucket || "remaining";
    const field = filters.field || "startAt";
    const value = filters.value;
    return (Array.isArray(tasks) ? tasks : []).filter((task) => {
        const dueDate = nativeDate(task?.dueDate);
        if (bucket === "completed" && task?.taskStatus !== "done") return false;
        if (bucket === "remaining" && task?.taskStatus === "done") return false;
        if (bucket === "overdue" && (task?.taskStatus === "done" || !dueDate || dueDate >= now)) return false;
        if (value === null || value === undefined || value === "") return true;

        if (["startAt", "dueDate", "createdAt", "updatedAt", "doneAt"].includes(field)) {
            return dateMatches(task?.[field], value, filters.dateRange, now);
        }
        if (field === "status") {
            if (value === "completed") return task?.taskStatus === "done";
            if (value === "incomplete") return task?.taskStatus !== "done";
            return true;
        }
        if (field === "assignee") return relationMatches(task?.assignee, value) || relationMatches(task?.assignee?.member, value);
        if (field === "creatorUser") return relationMatches(task?.creatorUser, value) || relationMatches(task?.creatorUser?.member, value);
        if (field === "members") return (task?.members || []).some((member) => relationMatches(member, value) || relationMatches(member?.member, value));
        if (field === "deliverables") return (task?.deliverables || []).some((deliverable) => relationMatches(deliverable, value));
        if (field === "task_type") return relationMatches(task?.task_type, value);
        return true;
    });
}

export function freeBoardTaskProgress(task) {
    const children = Array.isArray(task?.childrenTasks) ? task.childrenTasks : [];
    const checklist = Array.isArray(task?.checklist) ? task.checklist.filter((item) => item?.id && item?.title) : [];
    const rows = children.length ? children : checklist;
    if (!rows.length) return null;
    const completed = rows.filter((item) => children.length ? item?.taskStatus === "done" : item?.done === true).length;
    return {
        completed,
        total: rows.length,
        percent: Math.round((completed / rows.length) * 100),
    };
}
